import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { paths } from '@/paths';
import {
  Bank as BankIcon,
  CalendarBlank as CalendarBlankIcon,
  CalendarCheck as CalendarCheckIcon,
  CalendarX as CalendarXIcon,
  Clock as ClockIcon,
  CurrencyDollar as CurrencyDollarIcon,
  MapPin as MapPinIcon,
  Users as UsersIcon,
  Warning as WarningIcon,
  Wine as WineIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from '@/components/core/v2/toaster';

import type { AmenityReservation } from '@/types/v2/reservation';
import { cancelReservation, getMyReservations } from '@/api/reservations';

const STATUS_CONFIG: Record<
  string,
  { color: 'default' | 'warning' | 'success' | 'error' | 'info'; label: string }
> = {
  pending: { color: 'warning', label: 'Pending Approval' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
  completed: { color: 'info', label: 'Completed' },
  no_show: { color: 'error', label: 'No Show' },
  waitlisted: { color: 'info', label: 'Waitlisted' },
};

function toAmount(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

export function MyReservations(): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = React.useState(0); // 0=upcoming, 1=past, 2=all

  const { data, isLoading } = useQuery({
    queryKey: ['my-reservations'],
    queryFn: () => getMyReservations(),
  });

  const cancelMutation = useMutation({
    mutationFn: (reservationId: string) => cancelReservation(reservationId, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success('Reservation cancelled successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to cancel reservation');
    },
  });

  const reservations = data?.reservations || [];

  const upcoming = reservations.filter((r) => {
    if (!r.start_time) return false;
    const status = r.status ?? 'pending';
    return (
      dayjs.utc(r.start_time).local().isAfter(dayjs()) && ['pending', 'approved'].includes(status)
    );
  });

  const past = reservations.filter((r) => {
    if (!r.start_time) return true;
    const status = r.status ?? 'pending';
    return (
      dayjs.utc(r.start_time).local().isBefore(dayjs()) ||
      ['cancelled', 'rejected', 'completed', 'no_show'].includes(status)
    );
  });

  const filtered = filter === 0 ? upcoming : filter === 1 ? past : reservations;

  const handleCancel = (reservation: AmenityReservation) => {
    if (window.confirm('Are you sure you want to cancel this reservation?')) {
      cancelMutation.mutate(reservation.id);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Stats row */}
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
        <StatCard
          label="Upcoming"
          count={upcoming.length}
          variant="primary"
          icon={<CalendarCheckIcon size={20} weight="duotone" />}
        />
        <StatCard
          label="Pending"
          count={reservations.filter((r) => r.status === 'pending').length}
          variant="warning"
          icon={<ClockIcon size={20} weight="duotone" />}
        />
        <StatCard
          label="Past"
          count={past.length}
          variant="neutral"
          icon={<CalendarXIcon size={20} weight="duotone" />}
        />
      </Stack>

      {/* Filter tabs */}
      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}
      >
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={filter}
            onChange={(_, v) => setFilter(v)}
            sx={{
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                minHeight: 44,
              },
            }}
          >
            <Tab label={`Upcoming (${upcoming.length})`} />
            <Tab label={`Past (${past.length})`} />
            <Tab label={`All (${reservations.length})`} />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {filtered.length === 0 ? (
            <EmptyState filter={filter} />
          ) : (
            <Stack spacing={2}>
              {filtered.map((reservation) => (
                <ReservationRow
                  key={reservation.id}
                  reservation={reservation}
                  isPast={past.includes(reservation)}
                  onCancel={handleCancel}
                  cancelPending={cancelMutation.isPending}
                  onClick={() => navigate(paths.reservations.reservation(reservation.uuid ?? reservation.id))}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    </Stack>
  );
}

function StatCard({
  label,
  count,
  variant,
  icon,
}: {
  label: string;
  count: number;
  variant: 'primary' | 'warning' | 'neutral';
  icon: React.ReactNode;
}): React.JSX.Element {
  const variantStyles =
    variant === 'primary'
      ? { bgcolor: 'primary.light', color: 'primary.main' }
      : variant === 'warning'
        ? { bgcolor: 'warning.light', color: 'warning.main' }
        : { bgcolor: 'action.hover', color: 'text.secondary' };

  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 140px',
        py: 2,
        px: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...variantStyles,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {count}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

function EmptyState({ filter }: { filter: number }): React.JSX.Element {
  const messages: Record<number, { title: string; subtitle: string }> = {
    0: {
      title: 'No upcoming reservations',
      subtitle: 'Head over to the Amenities tab to browse and book.',
    },
    1: {
      title: 'No past reservations',
      subtitle: "You haven't had any reservations yet.",
    },
    2: {
      title: 'No reservations yet',
      subtitle: 'Start by browsing available amenities and making your first booking.',
    },
  };

  const msg = messages[filter] || messages[2];

  return (
    <Box sx={{ py: 6, textAlign: 'center', color: 'text.disabled' }}>
      <CalendarBlankIcon size={48} weight="duotone" />
      <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
        {msg.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 340, mx: 'auto' }}>
        {msg.subtitle}
      </Typography>
    </Box>
  );
}

function ReservationRow({
  reservation,
  isPast,
  onCancel,
  cancelPending,
  onClick,
}: {
  reservation: AmenityReservation;
  isPast: boolean;
  onCancel: (r: AmenityReservation) => void;
  cancelPending: boolean;
  onClick?: () => void;
}): React.JSX.Element {
  const startDate = reservation.start_time ? dayjs.utc(reservation.start_time).local() : null;
  const endTime = reservation.end_time
    ? dayjs.utc(reservation.end_time).local().format('h:mm A')
    : '—';
  const status = reservation.status ?? 'pending';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const hasFees =
    toAmount(reservation.deposit_amount_charged) > 0 ||
    toAmount(reservation.total_fee_amount_charged) > 0 ||
    toAmount(reservation.fee_amount_charged) > 0;
  const canCancel =
    ['pending', 'approved'].includes(status) &&
    reservation.start_time &&
    dayjs.utc(reservation.start_time).local().isAfter(dayjs());

  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        overflow: 'hidden',
        opacity: isPast ? 0.85 : 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': onClick ? {
          borderColor: 'primary.light',
          boxShadow: 2,
        } : undefined,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        {/* Date sidebar */}
        <Box
          sx={{
            width: { xs: '100%', sm: 80 },
            minHeight: { xs: 'auto', sm: 100 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 1.5, sm: 2 },
            px: 2,
            bgcolor: isPast ? 'action.hover' : 'primary.light',
            borderRight: { sm: '1px solid' },
            borderBottom: { xs: '1px solid', sm: 'none' },
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          {startDate ? (
            <Stack alignItems="center" spacing={0}>
              <Typography
                variant="overline"
                sx={{
                  lineHeight: 1.2,
                  fontWeight: 700,
                  color: isPast ? 'text.secondary' : 'primary.main',
                  fontSize: '0.65rem',
                }}
              >
                {startDate.format('MMM')}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1,
                  color: isPast ? 'text.secondary' : 'primary.main',
                }}
              >
                {startDate.format('D')}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
              >
                {startDate.format('ddd')}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 2.5, minWidth: 0 }}>
          {/* Header row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 1.5 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                {reservation.amenity?.name ?? 'Amenity'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{reservation.id != null ? String(reservation.id).slice(0, 8) : '—'}
              </Typography>
            </Box>
            <Chip
              label={cfg.label}
              size="small"
              color={cfg.color}
              sx={{ fontWeight: 600, fontSize: '0.7rem', flexShrink: 0, ml: 1 }}
            />
          </Stack>

          {/* Info grid */}
          <Stack
            direction="row"
            spacing={3}
            divider={<Divider orientation="vertical" flexItem />}
            sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}
          >
            {startDate && (
              <InfoChip
                icon={<ClockIcon size={14} />}
                text={`${startDate.format('h:mm A')} – ${endTime}`}
              />
            )}
            {reservation.guest_count != null && reservation.guest_count > 0 && (
              <InfoChip
                icon={<UsersIcon size={14} />}
                text={`${reservation.guest_count} guests`}
              />
            )}
            {reservation.alcohol_served && (
              <InfoChip icon={<WineIcon size={14} />} text="Alcohol" />
            )}
          </Stack>

          {/* Purpose */}
          {reservation.purpose && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {reservation.purpose}
            </Typography>
          )}

          {/* Notices */}
          {reservation.approval_notes && (
            <NoticeBox variant="success">
              <strong>Manager note:</strong> {reservation.approval_notes}
            </NoticeBox>
          )}
          {reservation.rejected_reason && (
            <NoticeBox variant="error">
              <strong>Reason:</strong> {reservation.rejected_reason}
            </NoticeBox>
          )}

          {/* Payment summary (inline) */}
          {hasFees && (() => {
            const total = toAmount(reservation.total_fee_amount_charged);
            const deposit = toAmount(reservation.deposit_amount_charged);
            const fee = toAmount(reservation.fee_amount_charged);
            const remaining = fee > 0
              ? (fee <= total && deposit > 0 ? Math.max(0, total - deposit) : fee)
              : 0;
            const hasDeposit = deposit > 0;
            const remainingCanBeDue = ['pending', 'approved'].includes(reservation.status);
            const hasRemaining = remaining > 0 && remainingCanBeDue;
            const paidInFull = hasDeposit && hasRemaining
              ? !!reservation.deposit_collected && !!reservation.fee_collected
              : hasDeposit
                ? !!reservation.deposit_collected
                : false;
            const feeDue = hasRemaining && !reservation.fee_collected && reservation.status === 'approved';

            return (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                {paidInFull ? (
                  <Chip
                    size="small"
                    icon={<CurrencyDollarIcon size={13} />}
                    label="Paid in Full"
                    color="success"
                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                  />
                ) : (
                  <>
                    {hasDeposit && (
                      <Chip
                        size="small"
                        variant={reservation.deposit_collected ? 'filled' : 'outlined'}
                        icon={<CurrencyDollarIcon size={13} />}
                        label={
                          reservation.deposit_refunded_at
                            ? `Deposit $${deposit.toFixed(2)} · Refunded`
                            : `Deposit $${deposit.toFixed(2)}${reservation.deposit_collected ? ' ✓' : ''}`
                        }
                        color={reservation.deposit_refunded_at ? 'info' : reservation.deposit_collected ? 'success' : 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                    {hasRemaining && (
                      <Chip
                        size="small"
                        variant={reservation.fee_collected ? 'filled' : 'outlined'}
                        icon={<CurrencyDollarIcon size={13} />}
                        label={`Fee $${remaining.toFixed(2)}${reservation.fee_collected ? ' ✓' : ''}`}
                        color={reservation.fee_collected ? 'success' : 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                  </>
                )}
                {feeDue && !isPast && (
                  <Chip
                    size="small"
                    label="Fee due — tap to pay"
                    color="warning"
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
            );
          })()}

          {/* Damage charge */}
          {toAmount(reservation.damage_charge_amount) > 0 && (
            <Box
              sx={{
                mt: 1.5,
                py: 1,
                px: 1.5,
                borderRadius: 1.5,
                bgcolor: 'error.light',
                border: '1px solid',
                borderColor: 'error.light',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'error.main' }}>
                <WarningIcon size={14} />
                <Typography variant="caption" fontWeight={600} color="error.main">
                  Damage Charge: ${toAmount(reservation.damage_charge_amount).toFixed(2)}
                </Typography>
              </Stack>
              {reservation.damage_charge_notes && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2.75 }}>
                  {reservation.damage_charge_notes}
                </Typography>
              )}
            </Box>
          )}

          {/* Cancel action */}
          {canCancel && (
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={(e) => { e.stopPropagation(); onCancel(reservation); }}
              disabled={cancelPending}
              sx={{
                mt: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                px: 1,
              }}
            >
              Cancel Reservation
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function InfoChip({ icon, text }: { icon: React.ReactNode; text: string }): React.JSX.Element {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
      {icon}
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        {text}
      </Typography>
    </Stack>
  );
}

function NoticeBox({
  variant,
  children,
}: {
  variant: 'success' | 'error';
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        py: 1,
        px: 1.5,
        borderRadius: 1.5,
        bgcolor: variant === 'success' ? 'success.light' : 'error.light',
        border: '1px solid',
        borderColor: variant === 'success' ? 'success.light' : 'error.light',
        mb: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}
